import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { ReactNode, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  isAnalyticsLoaded,
  useAnonymousAnalyticsTrackEvent,
  useAnonymousAnalyticsTrackPage,
} from '../../common/analytics';
import { IconArrowRight } from '../../common/design-tokens/Icons';
import { CirclesInviteStatus } from '../../common/types';
import { t } from '../../localization/LocaleContext';
import { MessageKey } from '../../localization/setup';
import { useCirclesInviteTokenInfo } from '../../remoteData/unauthenticated';
import LoadingAnimation from '../LoadingAnimation';
import { Container, SubmitButton, Text, Title } from './styledComponents';

const CircleTitle = styled(Title)`
  margin-bottom: ${({ theme }) => theme.spacers.xl};
`;

const MainParapgraph = styled(Text)`
  color: ${({ theme }) => theme.colors.blueDark100};
`;

const ErrorMessage = styled(Text)`
  color: ${({ theme }) => theme.colors.temperatureHot90};
  margin-bottom: ${({ theme }) => theme.spacers.l};
`;

const BuyLink = SubmitButton.withComponent('a');

function keyForErrorStatus(status: CirclesInviteStatus): MessageKey {
  switch (status) {
    case 'expired':
      return 'circles_invite_status_expired';
    case 'used':
      return 'circles_invite_status_used';
    default:
      return 'circles_invite_status_invalid';
  }
}

export default function CirclesInvite() {
  const analyticsTrackEvent = useAnonymousAnalyticsTrackEvent();
  useAnonymousAnalyticsTrackPage({
    name: 'Circles Invite',
    category: 'Public',
  });

  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t') ?? undefined;
  const campaign = searchParams.get('rc') ?? undefined;
  const [hasSeenValidToken, setHasSeenValidToken] = useState(false);
  const { isLoading, isError, data, error } = useCirclesInviteTokenInfo({
    token,
    campaign,
    onSuccess: (d) => {
      // We want to ignore errors that may be due to the user joining the Circle and leaving the browser tab open
      if (!hasSeenValidToken) {
        if (d.status === 'valid') {
          setHasSeenValidToken(true);
        } else {
          analyticsTrackEvent('Circles Invite Token Error', {
            status: d.status,
          });
        }
      }
    },
  });

  const shopURL = data?.rafURL || 'https://ouraring.com/?utm_medium=circles';
  const getRing = (
    <>
      <Text style={{ marginTop: theme.spacers.l }}>
        {t('circles_invite_no_oura_ring')}{' '}
        {data?.rafURL ? t('circles_invite_discount') : ''}
      </Text>
      <BuyLink
        href={shopURL}
        rounded
        size="xsmall"
        icon="end"
        onClick={(e) => {
          if (isAnalyticsLoaded()) {
            e.preventDefault();
            analyticsTrackEvent(
              'Circles Invite Shop Link Clicked',
              { isRAF: !!data?.rafURL },
              () => {
                window.location.href = shopURL;
              },
            );
          }
        }}
      >
        {t('circles_invite_get_ring_button')}
        <IconArrowRight aria-hidden />
      </BuyLink>
    </>
  );

  const hasStatusError = data?.status && data.status !== 'valid';

  if (isError || !token || hasStatusError) {
    return (
      <Container>
        <CircleTitle>{t('circles_invite_title')}</CircleTitle>
        <ErrorMessage>
          {t(keyForErrorStatus(data?.status || 'invalid'))}
          {error && (
            <>
              <br />
              {error.responseContent?.title || error.message}
            </>
          )}
        </ErrorMessage>
        <MainParapgraph>{t('circles_invite_intro')}</MainParapgraph>
        {getRing}
      </Container>
    );
  }

  if (isLoading || !data) {
    return (
      <Container>
        <LoadingAnimation />
      </Container>
    );
  }

  return (
    <Container>
      <CircleTitle>{t('circles_invite_title')}</CircleTitle>
      <MainParapgraph>{t('circles_invite_intro')}</MainParapgraph>
      <Text>
        {t('circles_invite_member_instructions', {
          link: (chunks: ReactNode) => (
            <a
              href={window.location.href}
              style={{ textDecoration: 'underline' }}
            >
              {chunks}
            </a>
          ),
        })}
      </Text>
      {getRing}
    </Container>
  );
}
